/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from 'helpers/store'
import {closeSecondaryModal} from 'helpers/actions'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiTextarea from 'components/Ui/UiTextarea'
import UiCounter from 'components/Ui/UiCounter'
import UiCurrencyInput from 'components/Ui/UiCurrencyInput'
import {OffersAction} from 'store/offers/OffersAction'
import {toast} from 'utils/toastUtils'
import {Controller, useForm} from 'react-hook-form'
import {zodResolver} from '@hookform/resolvers/zod'
import {z} from 'zod'
import {currencyNumberFormat, getPackagesCartPersonsCount} from 'utils/utility'
import {OfferCalculation} from 'types/entities/OfferCalculation'
import {PackageCategory} from 'types/enums/PackageCategory'
import {useTypedSelector} from 'helpers/reducers'
import Loader from 'components/Loader'
import AddItemToCalculationItemsList from 'components/Offers/AddItemToCalculationModalComponents/AddItemToCalculationItemsList'
import {getAddItemToCalculationItems, getDefaultAddItemQuantity, isCreditPackageItem, isFoodGroup} from 'utils/offers/addItemToCalculationUtils'
import type {PackageItem} from 'types/entities/PackageItem'
import UiSectionHeader from 'components/Ui/UiSectionHeader'
import PackageUpsellSlideOver from 'pages/Front/Booking/components/PackageUpsell/PackageUpsellSlideOver'
import {getOfferItemPresentation} from 'utils/offers/offerItemPresentation'
import UiRoundedContent from "components/Ui/UiRoundedContent";

const addItemSchema = z.object({
    package_item_id: z.number().min(1, 'Please select an item'),
    quantity: z.number().min(1, 'Quantity must be at least 1'),
    custom_text: z.string().optional(),
    price: z.number().min(0),
})

type AddItemFormData = z.infer<typeof addItemSchema>

interface AddItemToCalculationModalProps {
    offerId: number
    calculation: OfferCalculation
    onSuccess?: () => void
}

const AddItemToCalculationModal: React.FC<AddItemToCalculationModalProps> = ({ offerId, calculation, onSuccess }) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [loading, setLoading] = useState(true)
    const [selectedItem, setSelectedItem] = useState<PackageItem | null>(null)
    const portalRef = useRef<HTMLDivElement>(null)

    const availablePackageItems = useTypedSelector((state) => state.bookingState.bookingProcess.availablePackageItems)
    const seller_id = useTypedSelector((state) => state.sessionState.session?.uid) || 1
    const personsCount = getPackagesCartPersonsCount(calculation.packagesCart)

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { isSubmitting, errors },
    } = useForm<AddItemFormData>({
        resolver: zodResolver(addItemSchema),
        defaultValues: {
            package_item_id: 0,
            quantity: Math.max(1, personsCount || 1),
            custom_text: '',
            price: 0,
        },
    })



    const quantity = watch('quantity')
    const price = watch('price')
    const totalPrice = quantity * price
    const selectedItemPresentation = selectedItem ? getOfferItemPresentation(selectedItem) : null
    const quantityLabel =
        selectedItem &&
        (isCreditPackageItem(selectedItem) ||
            ('extra' in selectedItem && isFoodGroup((selectedItem as PackageItem).extra?.groupName)))
            ? t('Persons count')
            : t('Quantity')

    const handleItemSelect = (item: PackageItem) => {
        setSelectedItem(item)
        setValue('package_item_id', item.id)
        setValue('price', item.price / 100)
        setValue('quantity', getDefaultAddItemQuantity(item, personsCount))
        const itemPresentation = getOfferItemPresentation(item)
        const defaultCustomText = itemPresentation.custom_text || itemPresentation.title
        setValue('custom_text', defaultCustomText)
    }

    const onSubmit = async (data: AddItemFormData) => {
		console.log(data)
        if (!calculation.packagesCart) {
            toast.error(t('No packages cart found'))
            return
        }

        try {
            await dispatch(
                OffersAction.fetchAddOfferItem({
                    offer_id: offerId,
                    packages_cart_id: calculation.packagesCart.id,
                    package_item_id: data.package_item_id,
                    quantity: data.quantity,
                    custom_text: data.custom_text,
                    price: Math.round(data.price * 100),
                })
            )
            toast.success(t('Item added successfully'))
            dispatch(closeSecondaryModal())
            if (onSuccess) onSuccess()
        } catch (e) {
            console.error('Error adding item:', e)
            toast.error(t('Failed to add item'))
        }
    }

    const onError = (error) => {
		console.log('on erro',error)
        toast.error(t('Please fix validation errors'))
    }

    const allItems = useMemo(() => getAddItemToCalculationItems(availablePackageItems), [availablePackageItems])

	useEffect(() => {
		const loadPackages = async () => {
			if (!calculation.packagesCart) {
				toast.error(t('No packages cart found'))
				return
			}
			try {
				await dispatch(
					OffersAction.fetchPackagesForCalculation(
						calculation,
						calculation.packagesCart.category as PackageCategory,
						seller_id
					)
				)
			} catch (error) {
				console.error(error)
				toast.error(t('Failed to load available items'))
			} finally {
				setLoading(false)
			}
		}
		loadPackages()
	}, [calculation, dispatch, seller_id, t])
	
    if (loading) {
        return (
            <UiFormModalShell className="AddItemToCalculationModal" title={t('Add Item to Calculation')}>
                <div className="flex items-center justify-center h-64">
                    <Loader />
                </div>
            </UiFormModalShell>
        )
    }

    return (
        <UiFormModalShell className="AddItemToCalculationModal" title={t('Add Item to Calculation')} bodyClassName="px-6 py-6">
            <form onSubmit={handleSubmit(onSubmit, onError)} className="flex flex-col h-full min-h-0">
                <div ref={portalRef} />
                <div className="CreateOfferModal-content w-full flex flex-col space-y-6">
                    <div className="h-full">
                        <div className="SellerUpsells mx-auto w-full flex flex-col gap-4">
                            <UiSectionHeader title={t('Please select your extras')} />
                            <div className="flex flex-col gap-2 flex-1 min-h-0 overflow-y-auto pr-1">
                                <AddItemToCalculationItemsList
                                    items={allItems}
                                    selectedItemId={selectedItem?.id ?? null}
                                    onSelect={handleItemSelect}
                                />
                            </div>
                        </div>
                    </div>
                    {errors.package_item_id && (
                        <span className="text-xs text-red-500 mt-2">{errors.package_item_id?.message}</span>
                    )}
                </div>

                {selectedItem && selectedItemPresentation && (
                    <PackageUpsellSlideOver
                        actionsSlot={
                            <button
                                className="flex-1 rounded-2xl bg-blue-600 px-4 py-3 text-sm font-bold text-white shadow-sm hover:bg-blue-700 transition disabled:opacity-50"
                                type="submit"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? t('Adding...') : t('Add')}
                            </button>
                        }
                        customTextSlot={
                            <UiRoundedContent className={"AddItemToCalculationItems"}>
                                <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/70 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                    <p className="text-xs font-bold uppercase tracking-wide text-slate-700 dark:text-slate-300">
                                        {t('Custom Text')}
                                    </p>
                                    <button
                                        className="text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                        type="button"
                                        onClick={() => {
                                            const description = selectedItemPresentation.custom_text
                                                || selectedItemPresentation.title
                                            setValue('custom_text', description)
                                        }}
                                    >
                                        {t('Auto fill')}
                                    </button>
                                </div>
                                <div className="p-3">
                                    <Controller
                                        name="custom_text"
                                        control={control}
                                        render={({ field }) => (
                                            <UiTextarea
                                                {...field}
                                                placeholder={t('Enter custom text...')}
                                                rows={10}
                                                error={errors.custom_text?.message}
                                            />
                                        )}
                                    />
                                </div>
                            </UiRoundedContent>
                        }
                        descriptionHtml={selectedItemPresentation.descriptionHtml}
                        detailsSlot={
                            <UiRoundedContent className={"AddItemToCalculationItemTwo"}>
                                <div className="px-3 py-2 bg-slate-50 dark:bg-slate-800/70 border-b border-slate-200 dark:border-slate-700">
                                    <p className="text-xs font-bold uppercase tracking-wide text-slate-700 dark:text-slate-300">
                                        {t('Item Details')}
                                    </p>
                                </div>
                                <div className="p-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                                    <div className="sm:col-span-1">
                                        <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                                            {quantityLabel}
                                        </span>
                                        <div className="mt-2">
                                            <Controller
                                                name="quantity"
                                                control={control}
                                                render={({ field }) => (
                                                    <UiCounter
                                                        value={field.value}
                                                        onChange={(val) => field.onChange(val === '' ? 1 : val)}
                                                        min={1}
                                                    />
                                                )}
                                            />
                                        </div>
                                    </div>
                                    <div className="sm:col-span-1">
                                        <Controller
                                            name="price"
                                            control={control}
                                            render={({ field }) => (
                                                <UiCurrencyInput
                                                    label={t('Unit Price')}
                                                    value={field.value}
                                                    onValueChange={(value: string | undefined) => {
                                                        const numValue = value === undefined ? 0 : parseFloat(value)
                                                        field.onChange(isNaN(numValue) ? 0 : numValue)
                                                    }}
                                                    prefix="CHF "
                                                    decimalsLimit={2}
                                                    error={errors.price?.message}
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="sm:col-span-1">
                                        <UiCurrencyInput
                                            label={t('Total Price')}
                                            value={totalPrice}
                                            prefix="CHF "
                                            decimalsLimit={2}
                                            disabled={true}
                                        />
                                    </div>
                                </div>
                            </UiRoundedContent>
                        }
                        groupLabel={selectedItemPresentation.groupLabel}
                        imageSrc={selectedItemPresentation.imageSrc}
                        inCartCount={0}
                        isOpen={true}
                        onClose={() => setSelectedItem(null)}
                        title={selectedItemPresentation.title}
                        totalPrice={currencyNumberFormat(Math.round(totalPrice * 100))}
                        unitPrice={currencyNumberFormat(selectedItem.price)}
                        portalPosition={portalRef.current}
                    />
                )}
            </form>
        </UiFormModalShell>
    )
}

export default AddItemToCalculationModal
